import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="plan-change"
export default class extends Controller {
  static targets = [
    "modal",
    "overlay",
    "title",
    "description",
    "planName",
    "planPrice",
    "nextBillingDate",
    "confirmButton",
    "checkoutContainer",
    "confirmationDetails",
    "loadingSpinner",
    "actionButtons",
  ];

  static values = {
    changeUrl: String,
    checkoutUrl: String,
    verifyUrl: String,
    stripePublishableKey: String,
    requiresTokenizationTrial: Boolean,
    tokenizationAmount: String,
  };

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
    this.selectedPlan = null;
    this.checkoutInstance = null;
  }

  disconnect() {
    this.removeKeydownListener();
    this.cleanupCheckout();
  }

  open(event) {
    const {
      planType,
      planName,
      price,
      actionText,
      isUpgrade,
      isTrialEligible,
      requiresTokenization,
    } = event.params;

    this.selectedPlan = planType;
    const isTrial = this.parseBoolean(isTrialEligible);
    const isPaystackTrial =
      isTrial &&
      (this.parseBoolean(requiresTokenization) ||
        this.requiresTokenizationTrialValue);

    if (isTrial) {
      this.titleTarget.textContent = "Start Free Trial";

      if (isPaystackTrial) {
        // Paystack trial - requires authorization charge
        this.descriptionTarget.textContent = `Start your 7-day free trial of ${planName}. You can cancel anytime during the trial.`;
        this.confirmButtonTarget.textContent = "Continue to Payment";
        this.updatePaystackTrialBillingDetails(planName, price);
      } else {
        // Stripe trial - no upfront payment required
        this.descriptionTarget.textContent = `Start your 7-day free trial of ${planName}. No payment required now. You can cancel anytime during the trial.`;
        this.confirmButtonTarget.textContent = "Start Free Trial";
        this.updateTrialBillingDetails(planName, price);
      }
    } else {
      this.titleTarget.textContent = "Confirm Plan Change";
      this.descriptionTarget.textContent = `Are you sure you want to ${actionText.toLowerCase()}? This change will take effect immediately.`;
      this.confirmButtonTarget.textContent = this.parseBoolean(isUpgrade)
        ? "Confirm Upgrade"
        : "Confirm Change";
      this.updateBillingDetails(planName, price);
    }

    this.modalTarget.classList.remove("hidden");
    this.addKeydownListener();
  }

  close() {
    this.modalTarget.classList.add("hidden");
    this.selectedPlan = null;
    this.removeKeydownListener();
    this.cleanupCheckout();

    // Reset UI state
    if (this.hasCheckoutContainerTarget) {
      this.checkoutContainerTarget.classList.add("hidden");
    }
    if (this.hasConfirmationDetailsTarget) {
      this.confirmationDetailsTarget.classList.remove("hidden");
    }
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.remove("hidden");
    }
    this.hideLoading();
  }

  cleanupCheckout() {
    if (this.checkoutInstance) {
      this.checkoutInstance.destroy();
      this.checkoutInstance = null;
    }
  }

  async confirm() {
    if (!this.selectedPlan) return;

    // Show loading state
    this.showLoading();

    // Hide action buttons immediately
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.add("hidden");
    }

    try {
      // Create checkout session
      const response = await fetch(this.checkoutUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
        },
        body: JSON.stringify({
          plan_type: this.selectedPlan,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create checkout session");
      }

      // Check if this was a simple downgrade to flex (no checkout needed)
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }

      // For Paystack: redirect to authorization URL
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }

      // For Stripe: Initialize embedded checkout
      await this.initializeStripeCheckout(data.client_secret);
    } catch (error) {
      console.error("Checkout session error:", error);
      this.showError(error.message);
      // Show buttons again on error
      if (this.hasActionButtonsTarget) {
        this.actionButtonsTarget.classList.remove("hidden");
      }
    }
  }

  async initializeStripeCheckout(clientSecret) {
    // Load Stripe.js if not already loaded
    if (!window.Stripe) {
      await this.loadStripe();
    }

    const stripe = window.Stripe(this.stripePublishableKeyValue);

    // Hide confirmation details and action buttons, show checkout container
    if (this.hasConfirmationDetailsTarget) {
      this.confirmationDetailsTarget.classList.add("hidden");
    }
    if (this.hasActionButtonsTarget) {
      console.log("Hiding action buttons");
      this.actionButtonsTarget.classList.add("hidden");
    } else {
      console.warn("actionButtons target not found!");
    }
    if (this.hasCheckoutContainerTarget) {
      this.checkoutContainerTarget.classList.remove("hidden");
    }
    this.hideLoading();

    // Initialize embedded checkout
    this.checkoutInstance = await stripe.initEmbeddedCheckout({
      clientSecret,
    });

    // Mount checkout
    this.checkoutInstance.mount(this.checkoutContainerTarget);

    // Note: Stripe embedded checkout will automatically redirect to the return_url
    // after successful payment. No need to listen for events here.
  }

  async loadStripe() {
    return new Promise((resolve, reject) => {
      if (window.Stripe) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  showLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.remove("hidden");
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true;
      this.confirmButtonTarget.textContent = "Loading...";
    }
  }

  hideLoading() {
    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.add("hidden");
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = false;
    }
  }

  showError(message) {
    this.hideLoading();
    alert(message);
    this.close();
  }

  getCsrfToken() {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") || ""
    );
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }

  updateTrialBillingDetails(planName, price) {
    this._updateTrialBillingDetailsBase(planName, price, {
      description:
        "Start your free trial now. You'll be charged at the end of the trial period unless you cancel.",
      showAuthNotice: false,
    });
  }

  updatePaystackTrialBillingDetails(planName, price) {
    const amount = this.tokenizationAmountValue || "₦100";
    this._updateTrialBillingDetailsBase(planName, price, {
      description: `To verify your payment method, a small authorization charge of ${amount} will be made and immediately refunded. You'll be charged the full amount at the end of the trial period unless you cancel.`,
      showAuthNotice: true,
    });
  }

  // Private helper for shared trial billing logic
  _updateTrialBillingDetailsBase(
    planName,
    price,
    { description, showAuthNotice },
  ) {
    const today = new Date();
    const trialEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    this.planNameTarget.textContent = planName;
    this.planPriceTarget.textContent = price;
    this.nextBillingDateTarget.textContent = trialEnd.toLocaleDateString();

    const billingDescription = document.querySelector(
      '[data-plan-change-target="billingDescription"]',
    );
    if (billingDescription) {
      billingDescription.textContent = description;
    }

    const billingDateLabel = document.querySelector(
      '[data-plan-change-target="billingDateLabel"]',
    );
    if (billingDateLabel) {
      billingDateLabel.textContent = "Trial Ends:";
    }

    const authNotice = document.querySelector(
      '[data-plan-change-target="authorizationNotice"]',
    );
    if (authNotice) {
      authNotice.classList.toggle("hidden", !showAuthNotice);
    }
  }

  updateBillingDetails(planName, price) {
    const today = new Date();
    const nextBilling = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      today.getDate(),
    );

    this.planNameTarget.textContent = planName;
    this.planPriceTarget.textContent = price;
    this.nextBillingDateTarget.textContent = nextBilling.toLocaleDateString();

    // Update labels for paid context
    const billingDescription = document.querySelector(
      '[data-plan-change-target="billingDescription"]',
    );
    if (billingDescription) {
      billingDescription.textContent =
        "Switching plans starts a fresh billing cycle today. You'll be charged the full plan price on your next renewal.";
    }

    const billingDateLabel = document.querySelector(
      '[data-plan-change-target="billingDateLabel"]',
    );
    if (billingDateLabel) {
      billingDateLabel.textContent = "Next Renewal:";
    }
  }

  parseBoolean(value) {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      return value === "true";
    }
    return Boolean(value);
  }

  addKeydownListener() {
    document.addEventListener("keydown", this.handleKeydown);
  }

  removeKeydownListener() {
    document.removeEventListener("keydown", this.handleKeydown);
  }
}
